import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { DeviceModelEnum, getDeviceModelList, SelectableDeviceModel } from 'src/app/core/utils';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DictionaryService } from 'src/app/core/dictionary.service';
import { contains, equal, ODataFiltrableDataSource } from 'filtrable-data-source';
import { DeviceBoDtoNBK, DeviceDtoNBK, DeviceService } from 'src/app/api/nbk';
import { AuthService } from 'src/app/core/auth.service';
import { getDeviceTypeList, SelectableItem } from 'src/app/core/device.utils';
import { NgSelectComponent, NgOptionTemplateDirective, NgLabelTemplateDirective } from '@ng-select/ng-select';
import { filterAction } from 'src/app/core/filter-utils';
import { FormFilterResolver } from '../../filters/form-filter.resolver';


@Component({
    selector: 'app-device-filters',
    templateUrl: './device-filters.component.html',
    styleUrls: ['./device-filters.component.scss'],
    imports: [ReactiveFormsModule, NgSelectComponent, NgOptionTemplateDirective, 
      NgLabelTemplateDirective, TranslatePipe
    ]
})
export class DeviceFiltersComponent implements OnInit, OnDestroy {
  forServiceUser: boolean;
  forServiceAdminLainox: boolean;
  
  @Input()
  datasource: ODataFiltrableDataSource<DeviceDtoNBK>
  datasourceFilters: filterAction;

  deviceFilterForm: FormGroup;
  deviceModelList: SelectableDeviceModel[];
  deviceTypeList: SelectableItem[];
  
  modelIdentfierMap: Map<DeviceModelEnum, string[]> = new Map();
  modelIdentfierList: string[];

  unsubscribe$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private ts: TranslateService,
    private ds: DictionaryService,
    private authService: AuthService,
    private filterResolver: FormFilterResolver,
    private deviceService: DeviceService
  ) { }

  ngOnInit(): void {
    this.forServiceUser = this.authService.permissions.SERVICE
    this.forServiceAdminLainox = this.authService.permissions.LAINOX_SERVICE_ADMIN

    this.datasourceFilters = {
      name: () => {
        if ( this.forServiceAdminLainox ) {
          this.datasource.setCustomFilter(
            `(nameByUser/key eq '${this.authService.userId}') and contains(nameByUser/value, '${this.deviceFilterForm.value.name}')`
          );
        } else if ( this.forServiceUser ) {
          this.datasource.setCustomFilter(
            `contains(technicians/technicianDeviceName, '${this.deviceFilterForm.value.name}')`
          );
        } else {
          this.datasource.setFilter('name', contains(this.deviceFilterForm.value.name));
        }
      },
      serial: () => {
        this.datasource.setFilter(
          'serial', 
          contains(this.deviceFilterForm.value.serial));
      },
      deviceModel: () => {
        this.datasource.setFilter(
          'model', 
          equal(this.deviceFilterForm.value.deviceModel));
      },
      deviceType: () => {
        this.datasource.setFilter(
          'type', 
          equal(this.deviceFilterForm.value.deviceType));
      },
      owner: () => {
        this.datasource.setSearch(this.deviceFilterForm.value.owner);
      },
      online: () => {
        this.datasource.setFilter(
          'online', 
          equal(true));
      },
      loggedUser: () => {
        this.datasource.setFilter(
          'ownerIsLoggedIn', 
          equal(true));
      },
      modelIdentifier: () => {
        this.datasource.setFilter(
          'modelIdentifier', 
          equal(this.deviceFilterForm.value.modelIdentifier));
      },
    };

    this.deviceFilterForm = this.fb.group({
      name: [''],
      serial: [''],
      deviceModel: [null],
      deviceType: [null],
      owner: [''],
      online: [''],
      loggedUser: [null],
      modelIdentifier: [null]
    });
    this.deviceFilterForm.controls['deviceType'].disable();

    this.deviceModelList = getDeviceModelList();

    this.filterResolver.resolveFilters(
      'devices-filter',
      this.deviceFilterForm,
      () => this.search(),
      (form) => this.applyDeviceTypeFromModel(form.get('deviceModel')?.value)
    );

    this.deviceFilterForm.controls['online'].valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((online) => {
        if (!online) {
          this.deviceFilterForm.controls['loggedUser'].setValue(null);
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  search() {
    this.resetDatasourceFilter();
    Object.entries(this.deviceFilterForm.value).forEach(([key, value]) => {
      if ( value ) {
        this.datasourceFilters[key]();
      }
    });
    this.datasource.applyFilters();
  }

  clean() {
    this.deviceFilterForm.reset();
    this.search();
  }

  private resetDatasourceFilter() {
    this.datasource.removeCustomFilter();
    this.datasource.removeFilter('name');
    this.datasource.removeFilter('serial');
    this.datasource.removeFilter('model');
    this.datasource.removeFilter('type');
    this.datasource.removeFilter('online');
    this.datasource.removeFilter('online');
    this.datasource.removeFilter('ownerIsLoggedIn');
    this.datasource.removeFilter('modelIdentifier');
    this.datasource.removeSeach();
  }

  onDeviceModelChange($event: SelectableDeviceModel) {
    this.applyDeviceTypeFromModel($event?.id);
  }

  onDeviceTypeChange($event: SelectableItem) {
    if ($event && this.showModelIdentifierFilter()) {
      if ( !this.modelIdentfierMap.has($event.id) ) {
        this.deviceService.getModelIdentifiersByDeviceType($event.id)
        .pipe(
          takeUntil(this.unsubscribe$),
          tap((resp) => {
            this.modelIdentfierMap.set($event.id,resp);
            this.modelIdentfierList = resp;
          })
        ).subscribe();
      } else {
        this.modelIdentfierList = this.modelIdentfierMap.get($event.id)!;
      }
    }
  }

  showModelIdentifierFilter() {
    return this.deviceFilterForm.controls['deviceType'].value === 'COMPACT';
  }

  /** Updates Type list and enable/disable state from Model 
   * (used by onDeviceModelChange and after filter restore). 
   */
  private applyDeviceTypeFromModel(modelId: DeviceModelEnum | null | undefined): void {
    if (modelId && modelId !== 'NEO') {
      this.deviceTypeList = getDeviceTypeList(modelId);
      this.deviceFilterForm.controls['deviceType'].enable();
    } else {
      this.deviceTypeList = [];
      this.deviceFilterForm.controls['deviceType'].setValue(undefined);
      this.deviceFilterForm.controls['deviceType'].disable();
    }
  }

}
